import { getAllByIndex, getAll, putMany, putOne, clearStore } from "./db";

function normalizeTableRows(tableName, rows, userId) {
  const cachedAt = new Date().toISOString();

  return (rows || [])
    .filter(Boolean)
    .map((row) => {
      const recordId =
        row.id ||
        row.service_order_id ||
        row.invoice_id ||
        row.po_id ||
        row.client_id ||
        row.instrument_id ||
        row.technician_id ||
        row.code ||
        null;

      if (!recordId) return null;

      const updatedAt =
        row.updated_at ||
        row.edited_at ||
        row.certificate_created_at ||
        row.created_at ||
        row.date ||
        cachedAt;

      return {
        table_name: tableName,
        record_id: String(recordId),
        data: row,
        updated_at: updatedAt,
        cached_at: cachedAt,
        user_id: userId || null,
      };
    })
    .filter(Boolean);
}

export async function cacheTableRows(tableName, rows, userId) {
  const normalized = normalizeTableRows(tableName, rows, userId);
  await putMany("cached_records", normalized);
  await putOne("sync_metadata", {
    table_name: tableName,
    last_pull_at: new Date().toISOString(),
    last_successful_sync_at: new Date().toISOString(),
  });
}

export async function getCachedRows(tableName, userId = null) {
  const allRows = await getAllByIndex("cached_records", "by_table", tableName);
  const rows = (allRows || [])
    .filter((row) => !userId || row.user_id === userId)
    .map((row) => row.data)
    .filter(Boolean);
  return rows;
}

export async function getCachedRow(tableName, recordId, userId = null) {
  const allRows = await getAllByIndex("cached_records", "by_table", tableName);
  const match = (allRows || []).find(
    (row) =>
      String(row.record_id) === String(recordId) &&
      (!userId || row.user_id === userId)
  );
  return match?.data || null;
}

export async function getSyncMetadata(tableName) {
  return getAll("sync_metadata").then((rows) => {
    const found = (rows || []).find((row) => row.table_name === tableName);
    return found || null;
  });
}

export async function getLatestSyncAt(tableNames = []) {
  const rows = await getAll("sync_metadata");
  const relevant = Array.isArray(tableNames) && tableNames.length
    ? (rows || []).filter((row) => tableNames.includes(row.table_name))
    : rows || [];

  const timestamps = relevant
    .map((row) => row?.last_successful_sync_at || row?.last_pull_at || null)
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value));

  if (!timestamps.length) return null;
  return new Date(Math.max(...timestamps)).toISOString();
}

export async function fetchWithOfflineFallback({
  tableName,
  userId = null,
  fetcher,
}) {
  try {
    const rows = await fetcher();
    await cacheTableRows(tableName, rows, userId);
    return {
      rows: rows || [],
      source: "network",
      lastSyncAt: new Date().toISOString(),
    };
  } catch (error) {
    const cachedRows = await getCachedRows(tableName, userId);
    return {
      rows: cachedRows || [],
      source: cachedRows?.length ? "cache" : "empty",
      lastSyncAt: (await getSyncMetadata(tableName))?.last_successful_sync_at || null,
      error,
    };
  }
}

export async function clearOfflineCache() {
  await clearStore("cached_records");
  await clearStore("pending_operations");
  await clearStore("sync_metadata");
}

